import React from 'react';

import { Modal } from '@components/index';

const BRANDTECH_LOGO_URL =
  'https://bdnai-cms.trypncl.com/logo/brandtech-logo.svg';

interface BrandtechTermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BrandtechTermsModal: React.FC<BrandtechTermsModalProps> = ({
  isOpen,
  onClose,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    classOverride="w-[850px] p-8 flex flex-col gap-6"
  >
    <img src={BRANDTECH_LOGO_URL} alt="Brandtech Logo" className="w-40 h-20" />
    <div className="text-2xl font-bold text-blue-5">Terms of Service</div>
    <div className="text-sm font-medium">Last Updated: February 5, 2024</div>
    <div className="text-sm space-y-3 max-h-[60vh] overflow-y-auto pr-2">
      <p>
        These Terms and Conditions, together each relevant Order Form (if
        applicable), form the "Agreement" entered into by and between the
        Parties, which governs and conditions the nature and scope of the
        services to be provided by Pencil to Customer, including provision of
        access to the Platform for use in connection with the Authorised Brand
        (including to allow Customer create Outputs for that Authorised Brand).
      </p>
      <p>
        Where there is no Order Form, this Agreement is entered into by and
        between Customer and Pencil automatically when Customer concludes a
        purchase on the relevant order page and clicks to accept these Terms and
        Conditions. Where applicable, in respect of any person who concludes
        such order on behalf of another person or entity, you represent and
        warrant that you have the full and sufficient authority to accept the
        Terms and Conditions on behalf of such other person or entity and all
        references to "Customer" herein are noted to be references to that other
        person or entity.
      </p>
      <p>
        Where applicable, to the extent of any inconsistency or conflict between
        any provision(s) of these Terms and Conditions and any provision(s) of
        the Order Form, the relevant provision(s) of the Order Form shall govern
        and prevail.
      </p>
      <div className="font-bold">THE PARTIES AGREE AS FOLLOWS:</div>
      <div className="font-bold">1. INTERPRETATION</div>
      <p>
        1.1 The following definitions apply to this Agreement (including its
        recitals, above):
      </p>
      <ol className="list-decimal pl-7 space-y-4">
        <li>
          "Authorised Brand" means the relevant authorised brand in respect of
          which Customer is permitted under this Agreement to use the Platform
          and the Services, which is as identified by Customer in the ordering
          process for a subscription or, where applicable, set out in the
          relevant Order Form.
        </li>
        <li>
          "Authorised Users" means those of Customer's employees or other staff
          who are authorised from time to time by Pencil to use the Platform
          under this Agreement via certain of the Workspace(s) allocated to the
          Authorised Brand, and provided usernames, passwords and other
          necessary credentials by Pencil for the purpose of accessing and using
          those Workspace(s) via the Platform ("Access Credentials").
        </li>
        <li>
          "Beta Product" means any feature or functionality of the Platform,
          which is made available by Pencil on a 'beta', pre-release or
          evaluation basis.
        </li>
        <li>
          "Business Day" means any day which is not a Saturday, Sunday or public
          holiday in London, United Kingdom.
        </li>
        <li>
          "Claim" means any claim, demand, complaint, proceeding or other
          action.
        </li>
        <li>
          "Connected Application" means any system or application owned or
          controlled by Customer, which is connected to the Platform on behalf
          or at the direction of Customer (including through Customer's linking
          of its account for a Connected Application with the Platform using
          integrations made available as part of the Platform).
        </li>
        <li>
          "Connected Data" means any information or data (including any
          information or data relating to advertising performance) made
          available within the Platform via any Connected Applications or
          otherwise made available via Connected Applications and uploaded to
          the Platform (e.g., manually).
        </li>
        <li>
          "Content" means any text, images, audio, video or any other materials.
        </li>
        <li>
          "Customer" means the person or entity who is a counterparty to the
          Agreement (where applicable, as identified in the Order Form).
        </li>
        <li>
          "Documentation" means any manuals, instructions, user guides published
          by Pencil that describe the Platform and its use, operation, features
          and functionality.
        </li>
        <li>
          "Fees" means the fees for the relevant subscription and 'Service
          Package' identified on [https://www.trypencil.com/pricing] unless
          otherwise agreed pursuant to the Order Form.
        </li>
        <li>
          "Initial Term" means the relevant subscription term selected by
          Customer on the order page prior to purchase of a subscription to
          access the Platform or, where applicable, the Initial Term set out in
          the Order Form.
        </li>
        <li>
          "Inputs" means any Content input by Customer and its Authorised Users
          to be processed by the Platform to create Outputs (including any
          website properties input to byCustomer the Platform's 'URL scanning'
          tooling, together with any Content displayed on those properties).
        </li>
        <li>
          "Intellectual Property Rights" means all copyright and related rights,
          patents, rights to inventions, utility models, trade marks, service
          marks, trade, business and domain names, rights in trade dress or
          get‑up, rights in goodwill or to sue for passing off, unfair
          competition rights, rights in designs, rights in computer software,
          database rights, topography rights, moral rights, rights in
          confidential information (including know‑how and trade secrets),
          so-called 'personality rights' and 'publicity rights' (including
          rights to natural person's name, image and likeness), and any other
          intellectual property rights, in each case whether registered or
          unregistered and including all applications for and renewals or
          extensions of such rights, and all similar or equivalent rights or
          forms of protection in any part of the world.
        </li>
        <li>
          "Losses" means all losses, liabilities, damages, costs, claims,
          charges, regulatory fines, demands, actions and expenses (including
          legal and professional fees and disbursements and costs of
          investigation, litigation, settlement, judgment, interest and
          penalties).
        </li>
        <li>
          "Order Form" means an order form signed by the Parties for a 'Pencil
          Pro' Subscription to use the Platform to generate Content for the
          Authorised Brand plus associated Services, which incorporates these
          Terms and Conditions.
        </li>
        <li>
          "Outputs" means any visual or audio Content (including copy,
          storyboards, designs, voiceovers and other visual or audio Content),
          which is generated and returned by the Platform based on the Inputs
          through Customer's and its Authorised Users' permitted use of the
          Platform.
        </li>
        <li>
          "Pencil" means Pencil AI Limited, a company incorporated and
          registered under the laws of England and Wales with company number
          14789571 with its registered office at 151 Rosebery Avenue, London
          EC1R 4AB.
        </li>
        <li>
          "Pencil Technology" means collectively (i) the Platform, as updated
          from time to time; (ii) any and all other technology and software
          owned or used by Pencil, any associated algorithms, software code (in
          any form including source code and executable or object code), neural
          networks, models (including model architectures and algorithms
          (whether or not, instantiated in software code)), model weights,
          parameters, hyper parameters and coefficients, embeddings,
          calibrations, application programming interfaces, computer
          systems;(iii) any benchmarking, analytics or technical data relating
          to the performance or operation of the Platform; (iv) the
          Documentation as updated and amended from time to time; (v) as
          applicable in the context of a 'Pencil Standard' Subscription only,
          any Pencil Improvements; and (vi) any and all Intellectual Property
          Rights in or to each of (i)‑(iv).
        </li>
        <li>
          "Platform" means Pencil's cloud‑based software‑as‑a‑service 'Pencil'
          platform(including the Platform Interface) as described in the
          Documentation, which is owned, operated and/or licenced by Pencil,
          which may be used by Customer to generate Outputs, the features and
          functionalities of which shall vary dependent upon whether Customer
          has purchased a 'Pencil Pro' Subscription or a 'Pencil Standard'
          Subscription.
        </li>
        <li>
          "Platform Interface" means the standard web‑based interface required
          for Customer to access and use the Platform for the ordinary operation
          thereof under this Agreement, which is as notified to Customer from
          time to time (including, where relevant, https://app.trypencil.com or
          https://pro.trypencil.com or other https://trypencil.com domain as may
          be relevant in the circumstances).
        </li>
        <li>
          "Services" means those services which Pencil agrees to provide under
          this Agreement aspart of the relevant 'Service Package' selected by
          Customer (as those services are further described on
          https://trypencil.com), including where applicable as selected on the
          order page for a subscription or under the Order Form.
        </li>
        <li>
          "Renewal Term" means the relevant subscription term selected by
          Customer on the order page prior to purchase of a subscription to
          access the Platform or, where applicable, the Renewal Term set out in
          the Order Form.
        </li>
        <li>"Term" means the Initial Term and any Renewal Term(s).</li>
        <li>
          "Workspace" means a 'Workspace' made available via the Platform that
          is allocated to the Authorised Brand for a given market, geography or
          similar, and which is identified in the Order Form.
        </li>
      </ol>
      <p>
        1.2 To the extent not defined in this Section 1or elsewhere in these
        Terms and Conditions, the capitalised terms set out as headings in the
        Order Form shall have the meaning given to them in that Order Form.
      </p>
      <p>
        1.3 The following rules of interpretation shall apply in this Agreement:
        (a) any reference to thisAgreement terminating shall, where the context
        requires, include a reference to this Agreement terminating by expiry of
        the Term; (b) the words "include" and "including" (or similar) shall be
        construed as illustrative only and shall not limit the sense of the
        description, definition, phrase or term(s) that comes before the
        relevant term; and (c) any reference to the "Parties" means Pencil and
        Customer and "Party" shall mean one of them.
      </p>
      <p>
        1.4 Customer acknowledges and agrees that certain terms and conditions
        of the Agreement shall apply dependent upon the particular subscription
        package purchased by Customer, being either a "'Pencil Pro'
        Subscription" or a "'Pencil Standard 'Subscription" the primary features
        and functionalities of each of which are as described here:
        https://trypencil.com/pricing (or as further elaborated upon in the
        relevant Documentation associated with the respective subscription
        packages).
      </p>

      <div className="font-bold">2. PLATFORM</div>
      <p>
        2.1 Platform Access. Subject to Customer's and its Authorised Users'
        continued compliance with this Agreement (including payment of all Fees
        due and payable in accordance with Section 4), Pencil hereby grants
        Customer a limited, personal, non‑exclusive and non‑sublicensable right
        during the Term: (a) for Customer and its Authorised Users to use the
        ordinary features and functionalities of the Platform as part of
        Customer's internal business operations via theWorkspace(s) in
        connection with the Authorised Brand; and (b) only where permitted as
        part of the 'Pencil Pro' Subscription, for Customer to apply its trade
        marks, trade names, service marks, trade dress, logos and 'look and
        feel' to the Platform using the ordinary features and functionalities
        made available by Pencil for that purpose ("Marks").
      </p>

      <p>
        2.2 'Pencil Pro' – No Training. In respect of Customers with a 'Pencil
        Pro' Subscription, Pencil will not use and will not permit any providers
        of Third‑Party Services (as defined below) to use any Inputs, Outputs,
        nor any Connected Data, to develop or improve the Pencil Technology
        and/or any Third-Party Services (including any models, algorithms, and
        systems that are part of or integrated with the Platform); provided
        that, such Inputs, Outputs and Connected Data will be used to train and
        improve any dedicated predictive model associated with any Workspace(s)
        and which is made accessible via the Platform as part of a 'PencilPro'
        Subscription (such models, each a "Dedicated Model"). The Dedicated
        Models shall not be made available by Pencil for use by any other Pencil
        customers, and no Dedicated Model shall be made available to or used by
        Customer or its Authorised Users other than via the relevant Workspace
        to which that model is dedicated.
      </p>

      <p>
        2.3 Availability. Pencil will use commercially reasonable efforts to
        maintain the operation and availability of the Platform (subject always
        to (a) any scheduled or emergency maintenance or upgrades; and/or (b)
        circumstances outside Pencil's reasonable control (including any Force
        Majeure Event); and/or (c) resulting from any breach, non‑performance or
        any other default by Customer under this Agreement).
      </p>

      <p>
        2.4 Authorised Users. Customer is solely responsible for the acts and
        omissions of its Authorised Users. Customer shall immediately notify
        Pencil of any unauthorised use of any Authorised User's Access
        Credentials. Customer shall: (a) ensure that: (i) only Authorised Users
        access the Platform; and (ii) each Authorised User maintains the
        confidentiality of the Access Credentials; (b) not allow anyAccess
        Credentials allocated to an Authorised User to be used by more than one
        individual Authorised User; (c) immediately notify Pencil if any
        Authorised User ceases to be employed or otherwise engaged by Customer;
        and (d)Authorised Users only access Workspace(s) for which they have
        been allocated Access Credentials.
      </p>

      <p>
        2.5 Additional Services. In addition to making available the Platform,
        Pencil shall also provide the Services to Customer. Pencil shall provide
        any such Services in accordance with reasonable care and skill
        consistent with good industry standards and practices.
      </p>

      <p>
        2.6 Beta Testing. From time to time, Customer may have the option to
        participate in a programme where Customer and its Authorised Users are
        permitted at no additional cost to access certain Beta Products.
        Customer may only use the Beta Products to internally evaluate and test
        such Beta Products and for no other purpose whatsoever. The Beta
        Products are not generally available and are provided by Pencil "as is",
        without any conditions, warranties, representations, undertakings, or
        other terms either express or implied whatsoever (including as to
        non‑infringement, merchantability, satisfactory quality, use of
        reasonable skill and care or fitness for any particular purpose).
        Furthermore, Pencil does not provide any indemnities or service level or
        availability commitments whatsoever in relation to the Beta Products
        (e.g., Sections 2.3 and 10.1 do not apply in respect of Beta Products).
        As Pencil sees fit inits sole discretion, it may from time to time
        impose, and Customer shall comply with, certain additional restrictions
        or limitations on Customer's and its Authorised Users' use of the Beta
        Products. Pencil may terminate Customer's access to any Beta Product(s)
        at any time with or without notice, at which point Customer and its
        Authorised Users must stop using the Beta Product.Customer shall not
        disclose to any third party or publish (including via the internet or
        social media) details of any Beta Product (including details of its any
        evaluation or testing (including any Outputs, results, screenshots,
        questions, or workflows) of the Beta Product) without Pencil's prior
        written consent.
      </p>

      <div className="font-bold">3. OWNERSHIP AND LICENSING</div>
      <p>
        3.1 Pencil Ownership. As between the Parties,Pencil retains sole
        ownership of all right, title and interest, including allIntellectual
        Property Rights, in and to: (a) the Pencil Technology; and (b) unless
        and to the limited extent expressly agreed otherwise under the Order
        Form, any work product, deliverables, materials, content, software,
        technology or similar produced, conceived, created, developed as part of
        or in connection with the Services ("WorkProduct", which excludes, for
        the avoidance of doubt, Outputs). Customer will not acquire any right,
        title or interest in or to the Pencil Technology nor Work Products
        (subject only to the limited licences expressly granted in Sections 2.1
        and 3.2), and Pencil reserves all rights therein and thereto not
        expressly granted in this Agreement.
      </p>

      <p>
        3.2 Work Products Licence. Pencil hereby grants to Customer a limited,
        non‑exclusive, worldwide, perpetual, royalty‑free and fully paid‑up
        licence to use the Work Products as part of Customer's ordinary
        commercial operations.
      </p>

      <p>
        3.3 Customer Ownership. As between the Parties, Customer retains sole
        ownership of all right, title and interest, including all Intellectual
        Property Rights, in and to all Inputs,Outputs, Connected Data and its
        Marks.
      </p>

      <p>
        3.4 Licence to Pencil. Customer hereby grants to Pencil a non‑exclusive,
        worldwide, royalty‑free and fully paid‑up licence during the Term to use
        the Inputs, Outputs, Connected Data, any Connected Applications, the
        Authorised Brand (including the name of the Authorised Brand, any trade
        marks, trade names, service marks, trade dress, logos and 'look and
        feel' of or associated with the Authorised Brand) and its Marks, and
        under any other relevant Intellectual Property Rights owned or licenced
        by Customer, as necessary to provide, operate and make available the
        Platform to Customer and/or to provide the Services.
      </p>

      <p>
        3.5 'Pencil Standard' – Improvement and Training Licence. In respect of
        Customers with a 'Pencil Standard' Subscription, Customer hereby grants
        Pencil a non‑exclusive, fully sub‑licensable, transferable, worldwide,
        perpetual and irrevocable, royalty‑free and fully paid‑up licence to use
        Inputs, Outputs and Connected Data on an anonymised, de‑identified or
        aggregated basis to develop or improve the Pencil Technology and related
        technology, products and improvements ("Pencil Improvements"), including
        to train models that are part of the Pencil Technology and/or to create
        new, improved or augmented algorithms, models and model weights.
      </p>

      <div className="font-bold">4. FEES</div>
      <p>
        4.1 Payments. Customer shall pay the Fees in advance in accordance with
        this Section 4and any relevant terms of the Order Form.
      </p>

      <p>
        4.2 'Pencil Pro' – Invoicing and Payment. In respect of Customers with a
        'Pencil Pro' Subscription, Pencil shall issue Customer with invoices in
        respect of any Fees that come due and payable and Customer shall pay the
        amounts shown in all such invoices in full into the Pencil Bank Account
        (or such other account as directed by Pencil) within thirty (30)days of
        the invoice date, unless otherwise agreed in the Order Form.
      </p>

      <p>
        4.3 'Pencil Standard'– Payment Processing and Payment. In respect of
        Customers with a 'Pencil Standard' Subscription: (a) Customer
        acknowledges that Pencil uses a third‑party service provider for payment
        services (e.g., card acceptance, merchant settlement, and related
        services) ("Third‑Party Payment Processor");(b) Pencil shall process
        Customer's payment for the Fees (including anyFees payable in respect of
        ant Renewal Term(s)) via its appointed Third‑Party Payment Processor;
        (c) Customer shall provide complete and accurate payment and billing
        information and a valid and authorised payment method by which it shall
        pay the Fees; (d) Customer hereby authorises Pencil, and its
        Third‑PartyPayment Processor appointed from time to time, to charge such
        payment method for all Fees coming due and payable to Pencil under and
        in accordance with thisAgreement and agrees that no additional notice or
        consent is required for such purpose; (e) if Customer's payment method
        is rejected or payment for the Fees otherwise cannot be processed via
        that payment method, then Pencil will notify Customer andCustomer will
        timely pay the Fees by another mechanism agreeable to Pencil, pending
        which Pencil may suspend Customer's and its Authorised Users access to
        the Platform and/or provision of the Services as described in Section
        4.6; and (f) Customer acknowledges and agrees that this Agreement shall
        automatically renew and Customer's payment method shall continue to
        charge the then-current Fees for its 'Pencil Standard' Subscription
        unless and until this Agreement is terminated in accordance with its
        terms (including in accordance with Section 12.1(z)).
      </p>

      <p>
        4.4 Fees. All Fees: (a) shall be payable in the currency shown on
        [https://www.trypencil.com/pricing] or otherwise displayed to Customer
        during the ordering process (as applicable) unless otherwise agreed in
        the Order Form; (b) are non‑cancellable and non‑refundable unless and to
        the extent expressly provided otherwise in this Agreement; and (c) are
        exclusive of any and all taxes (including value added tax).
      </p>

      <p>
        4.5 Fee Changes. Pencil reserves the right to increase the Fees
        following the Initial Term, and each Renewal Term thereafter, provided
        that Pencil will provide notification of any such increase at least
        ninety (90) days in advance of the end of the Initial Term or
        then‑current Renewal Term (as applicable).
      </p>

      <p>
        4.6 Late payments. In addition to any other rights or remedies available
        to Pencil, if Customer fails to pay any sums by the due date: (a) any
        portion of any sums that is not paid when due and payable will accrue
        interest equal to the higher of: (i) six percent (6%) per annum above
        the Bank of England's base lending rate from time to time; or (ii) the
        default statutory rate applicable from time to time under the Late
        Payment of Commercial Debts (Interest) Act 1998, accruing on a daily
        basis and being compounded quarterly, from the time the indebtedness
        arose, with interest on all overdue interest accruing at the same rate
        and calculated and payable in the same manner until fully paid, whether
        before or after judgment; and (b) Pencil may suspend the provision of
        any access to the Platform and/or any and all other parts of the
        Services upon fourteen (14) days' prior notice until payment of the sums
        is made by Customer in full without liability to Customer or prejudice
        to any other rights or remedies available to Pencil in the
        circumstances.
      </p>

      <div className="font-bold">5. RESTRICTIONS</div>
      <p>
        Customer shall not: (a) use the Pencil Technology, any Outputs and/or
        any Work Products in any manner or for any purpose that is inconsistent
        with this Agreement (including using the Platform and/or any Outputs in
        connection with any other brand than the Authorised Brand and/or
        allowing Authorised Users to access Workspace(s) for which they do not
        have active Access Credentials); (b) provide or otherwise make the
        PencilTechnology available to any third parties (other than to
        Authorised Users as permitted under Section 2.1);(c) use the Pencil
        Technology to create, market or distribute any product or service that
        is similar to, competitive with, or otherwise replicates or seeks to
        replicate any features or functionalities of, the Platform or any other
        part of the Pencil Technology; (d) introduce to the Pencil Technology
        any 'backdoor', 'drop dead device', 'time bomb', 'Trojan horse', 'virus'
        or 'worm' or any other equivalent code, software routine or instructions
        designed or intended to disrupt, disable, harm or otherwise impede in
        any manner the operation of the Pencil Technology or any device or
        system owned or controlled by Pencil or any third party, or which
        otherwise may damage or destroy any data or file; (e) use the Pencil
        Technology in a manner(including through query access) to gather
        information as to infer the operation of, or replicate, all or any part
        of any machine learning models, datasets or other technologies forming
        part of the Pencil Technology (including as part of any so‑called 'model
        extraction', 'model inference' or 'model inversion' techniques or
        similar); (f) modify, copy, resell, rent, lease, sub‑licence, load,
        merge, adapt or translate the Pencil Technology ; (g) contest, challenge
        or otherwise make any claim or take any action adverse to Pencil's
        ownership of, or interest in, the Pencil Technology ; (h) re‑use,
        disseminate, copy, or otherwise use the Pencil Technology in a way that
        infringes, misappropriates, or violates any Intellectual Property Rights
        or other right of Pencil or any third party; (i) remove, alter or
        obscure any trademark notice, copyright notice or any other proprietary
        notice from the Pencil Technology, unless and to the extent permitted in
        accordance with Section 2.1(b);(j) circumvent, disable, or interfere
        with security‑related or rate‑limiting features of the Pencil Technology
        or features that limit, prevent or restrict use, access to, or copying
        of the Pencil Technology, or that enforce limitations on the use of the
        Pencil Technology; (k) reverse engineer ,decompile, unbundle,
        disassemble, or create derivative works based on the whole or any part
        of the Pencil Technology unless, and to the limited extent that,
        applicable laws of Customer's jurisdiction require Pencil to give
        Customer the right to do so to obtain information necessary to render
        the Pencil Technology interoperable with other software; provided,
        however, that Customer must first request such information from Pencil,
        and Pencil may (in its sole discretion) either provide such information
        to Customer or impose reasonable conditions on such use of the source
        code for the Pencil Technology to ensure that Pencil and its licensors'
        proprietary rights in the source code for the PencilTechnology are
        protected; (l) impose (or which may impose, in Pencil's sole discretion)
        an unreasonable or disproportionately large load or strain on
        thePlatform; (m) automatically publish or make generally available any
        Outputs without human intervention, review and approval; (n) use the
        Platform otherwise than in strict accordance with any terms, conditions
        and policies of the provider of any Third‑Party Service or Connected
        Application (as applicable) ("Third-PartyTerms"); (o) use the Pencil
        Technology, any Outputs and/or any Work Products in any manner or for
        any purpose that contradicts the restrictions set forth in Appendix A
        (Use‑basedRestrictions); or (p) take any action designed or intended to
        do any of the foregoing. Pencil may immediately suspend the provision of
        any access to thePlatform and/or any and all other parts of the Services
        for any breach of the foregoing restrictions.
      </p>

      <div className="font-bold">6. WARRANTIES AND DISCLAIMERS</div>
      <p>
        6.1 Limited Warranties. Each Party warrants and represents on an ongoing
        basis that it has, and undertakes that it shall continue to have for the
        duration of this Agreement at all relevant times, the requisite power,
        capacity and authority to enter into this Agreement, to perform and
        carry out its the obligations under this Agreement and to grant any
        licences granted under this Agreement. Pencil warrants that, during the
        Term, the Platform will materially conform to the then‑current
        Documentation when used in accordance with that Documentation and the
        provisions of this Agreement; provided that such warranty shall not
        apply: (a) in the event that Customer or any Authorised User has reached
        any of the restrictions outlined in Section 5; or (b) to any defects or
        issues arising as a result of any use of the Platform in combination
        with other products, hardware, equipment, software, or data not
        expressly authorised by Pencil to be used with the Platform.
      </p>

      <p>
        6.2 Disclaimer. Except as expressly provided in this Agreement, the
        Platform, Services, Work Products and the Outputs are provided "as is"
        and to the fullest extent permitted by law, Pencil disclaims all other
        conditions, warranties, representations, undertakings, or other terms
        which might have effect between the Parties with respect to the
        Platform, Services, Work Products and/or theOutputs, or otherwise be
        implied or incorporated into this Agreement, whether by statute, common
        law, custom or otherwise, including any implied conditions, warranties,
        undertakings or other terms relating to satisfactory quality, reasonable
        skill and care, fitness for any particular purpose, non‑infringement,
        ability to achieve a particular result or arising from course of dealing
        or usage of trade. Furthermore, Customer acknowledges that Pencil: (a)
        gives no express or implied warranty, representation, nor undertakings
        that the Outputs do not infringe third party rights (including
        Intellectual Property Rights), nor that any contents, findings or
        materials created or otherwise derived from use of Platform will
        constitute valid, subsisting or enforceable Intellectual Property Rights
        and/or be capable of registration in any jurisdiction; (b) does not
        warrant, represent, endorse, support or guarantee the completeness,
        truthfulness, accuracy, legality, originality, reliability, performance,
        appropriateness, fitness for purpose or any other attributes of any
        Outputs; and (c) shall not be, unless and to the extent expressly agreed
        otherwise aspart of any Services, responsible for reviewing or
        attempting to verify the accuracy or currency of any Outputs. Customer
        shall be solely responsible for Customer's use of the Platform, the
        Services and any Output and, unless and to the extent expressly agreed
        otherwise as part of any Services, for evaluating the fitness of any
        Output as appropriate for Customer's specific use case or any other
        purpose.
      </p>

      <div className="font-bold">7. CUSTOMER DEPENDENCIES</div>
      <p>
        Customer shall: (a) implement and maintain effective security policies
        and procedures to prevent unauthorised disclosure of Access Credentials
        and unauthorised access to the Platform; (b) secure any systems and
        devices Customer uses to access the Platform; (c) ensure that all
        Authorised Users of the Platform have installed, and access the Platform
        via the Platform Interface, using a supported version of the internet
        browsers and operating systems that are listed in the Documentation as
        supported by Pencil from time to time; (d) ensure that it has an
        internet connection with adequate bandwidth for Authorised Users to
        access and use the Platform; (e) ensure that it maintains connectivity
        to the extent necessary to prevent network performance degradation; and
        (f) maintain all necessary back‑up copies of all Inputs, Outputs and
        Connected Data, and Customer acknowledges that it, and not Pencil, is
        solely responsible for doing so.
      </p>

      <div className="font-bold">
        8. THIRD-PARTY SERVICES AND CONNECTED APPLICATIONS
      </div>
      <p>
        8.1 Third-Party Services. Certain elements of the Platform may
        incorporate functionality of, or enable access to, certain third‑party
        tools, systems, applications and/or platforms (including certain
        third‑party artificial intelligence models, algorithms, and platforms)
        ("Third‑Party Services").The incorporation of the functionality of, or
        Customer's and its Authorised Users' ability to access, certain
        Third-Party Services via the Platform will vary depending upon the
        particular 'Magic Tools' enabled or disabled by Customer through its
        configuration of the Platform and/or particular Workspaces. Customer
        acknowledges that by using the Platform(including through its
        configuration of relevant 'Magic Tools', as and where applicable), it is
        instructing Pencil to share any Inputs, Outputs and/or Connected Data
        with the relevant providers of such Third‑Party Services to the extent
        necessary to facilitate such functionality or access. Customer
        acknowledges that Customer and/or its Authorised Users may be required
        to enter into certain agreements with the providers of such Third‑Party
        Services and in the event the relevant agreements are not entered into,
        Customer and/or its Authorised Users may be unable to access: (a) such
        Third‑Party Services; and/or(b) any or all of those elements or
        functionalities of the Platform that rely upon such Third‑Party
        Services.
      </p>

      <p>
        8.2 Connected Applications. Certain elements of the Platform may permit
        or enable Customer and/or its Authorised Users to link or otherwise
        connect or integrate Customer's Connected Applications to the Platform
        (including via custom or pre-built integrations). By connecting any
        Connected Application to the Platform, Customer (a) represents and
        warrants that it is entitled to link or otherwise connect the Connected
        Application to the Platform; (b) instructs Pencil to access and/or share
        any Inputs, Outputs and/or Connected Data with the relevant providers of
        such Connected Applications and (c) is in agreement with the relevant
        third-party services and connected applications terms outlined in, but
        not limited to, Appendix B. Customer acknowledges and agrees that Pencil
        may access Connected Data so that it may be used in accordance with the
        terms of this Agreement.
      </p>

      <p>
        8.3 General. Customer acknowledges that such Third‑Party Services and
        any Connected Applications are not under Pencil's control and Pencil is
        not responsible for those Third‑Party Services nor Connected
        Applications, and Pencil will have no liability for any unavailability
        or failure of any Third‑Party Service or Connected Application, or any
        third-party provider's decision to discontinue, suspend or terminate any
        Third‑Party Service or Connected Application. To the fullest extent
        permitted by law, Customer expressly disclaims any and all express or
        implied conditions, warranties, representations, undertakings, or other
        terms of any nature relating to Third‑Party Services or Connected
        Applications.
      </p>

      <div className="font-bold">9. DATA PROTECTION</div>
      <p>
        The Pencil Data Processing Addendum shown from time to time at:
        https://www.trypencil.com/legals/data-processing-addendum or any
        successor webpage (the "DPA")shall apply in accordance with its terms in
        relation to Pencil's Processing of Customer Personal Data on Customer's
        behalf in performance of this Agreement. If and where the DPA applies in
        accordance with the foregoing, to the extent of any inconsistency or
        conflict between any provision(s) of this Agreement and any provision(s)
        of the DPA, the relevant provision(s) of the DPA shall govern and
        prevail in preference to any relevant provision(s) of this Agreement
        insofar as they relate to Pencil's Processing of Customer Personal Data.
        Any capitalised terms used in this Section 9, but not defined in this
        Agreement, shall have the meaning given to them in the DPA.
      </p>

      <div className="font-bold">10. INDEMNITIES</div>
      <p>
        10.1 Pencil Indemnities. Subject to Sections 10.2and 10.3, Pencil shall
        indemnify: (a) Customers with a 'Pencil Pro' Subscription from and
        against Losses incurred by such Customers asa result of amounts awarded
        in judgment or settlement of any third party Claim against Customer
        alleging that any Output infringes, misappropriates or violates the
        Intellectual Property Rights of that third party; and(b) Customer from
        and against Losses incurred by Customer as a result of amounts awarded
        in judgment or settlement of any third party Claim against Customer that
        Customer's use of the Platform (excluding always any Outputs or any use
        thereof) within the scope of the rights granted to Customer under this
        Agreement, infringes, misappropriates or violates the Intellectual
        Property Rights of a third party.The obligations set forth in this
        Section 10.1 as they may apply in the circumstances shall constitute
        Pencil's entire liability and Customer's sole remedy for any actual or
        alleged infringement, misappropriation or violation of anyIntellectual
        Property Rights of a third party arising out of or in connection with
        this Agreement.
      </p>

      <p>
        10.2 Customer obligations. Customer shall: (a) notify Pencil in full,
        accurate and complete detail in writing promptly (and in any event
        within two (2) Business Days) after it becomes aware of any event or any
        Claim, which it believes may give rise to a claim for indemnification
        under Section 10.1 (an "Indemnified Claim"); (b) allowPencil sole
        authority to control the defence and settlement of any Indemnified
        Claim; (c) provide Pencil with all reasonable cooperation in the defence
        of such Indemnified Claim; and (d) not settle or compromise any
        Indemnified Claim or make any admission of liability without the express
        prior written consent ofPencil. In relation to any Indemnified Claim, at
        its option, Pencil may elect to: (x) modify or replace all or any
        relevant part of the Platform so that it becomes non‑infringing; (y)
        obtain any required licence(s) to use the applicable third‑party
        Intellectual Property Rights; or (z) terminate this Agreement on written
        notice to Customer and refund to Customer any pre‑paid Fees pro‑rated to
        reflect the remainder of the Initial Term or then‑current Renewal Term
        (as applicable).
      </p>

      <p>
        10.3 Exclusions. Pencil shall have no liability whatsoever to Customer
        under either indemnity set out in Section 10.1 (as and where applicable)
        in respect of any Indemnified Claims based on or arising directly or
        indirectly as a result of: (a) any breach of this Agreement (including
        the restrictions outlined in Section 5);(b) any use of the Platform in
        combination with other products, hardware, equipment, software, data or
        other Content not expressly authorised by Pencil to be used with the
        Platform; (c) any modification of the Platform by any person other than
        Pencil or its expressly authorised agents or any third party that
        performs any element of the Services for or on behalf of Pencil; (d) the
        Inputs and/or Connected Data and/or any use thereof by Pencil, Customer
        or any third party; (e) any Third‑Party Services (save to the limited
        extent, in respect of Customers with a 'Pencil Pro' Subscription only,
        such liability arises under Section 10.1(a)); (f) any Connected
        Applications; (g) any Beta Products (including any use of the Platform
        in conjunction with any Beta Products); (h) in respect of Customers with
        a 'Pencil Standard' Subscription only, any Outputs or any use whatsoever
        of any Outputs; or(i) in respect of Customers with a 'Pencil Pro'
        Subscription only, who may benefit from the indemnity set out in Section
        10.1(a): (i) any modification to any Output by any person other than
        Pencil or its expressly authorised agents or any third party that
        performs any element of the Services for or on behalf of Pencil; (ii)
        use of any Output by any person in connection with any Content
        (including Customer's Inputs) that, alone or in combination, infringe,
        misappropriate or violate any third party's Intellectual Property
        Rights; or (iii) Customer's or its Authorised Users' intentional,
        negligent or reckless generation of an Output that infringes,
        misappropriates or violates any third party's Intellectual Property
        Rights.
      </p>

      <p>
        10.4 Customer Indemnity. In respect of Customers with a 'Pencil
        Standard' Subscription only, Customer agrees to indemnify Pencil on
        demand for, and hold harmless Pencil from and against, any and all
        Losses suffered or incurred by Pencil arising out of or in connection
        with any third-party Claim brought, made or threatened against Pencil
        that: (a) any Inputs, Outputs, Connected Data or Customer's Marks
        infringes, misappropriates or violates the rights (including
        Intellectual Property Rights) of any third party; and/or (b) otherwise
        arises or results from: (i) the use, licensing, sale, development,
        import, export, exploitation, modification, enhancement, improvement of
        or to, and/or or reliance upon, any Outputs; or (ii) Customer's breach
        of this Agreement and/or violation of applicable law.
      </p>

      <div className="font-bold">11. LIMITATION OF LIABILITY</div>
      <p>
        11.1 Unlimited Liability. Nothing in this Agreement limits or excludes
        the liability of either Party: (a) for death or personal injury caused
        by its negligence; (b) for fraud or fraudulent misrepresentation;(c)
        under the indemnities under Section 10.1(b) and Section 10.4; (d) for
        any other act, omission, or liability which may not be limited or
        excluded by applicable law; or (e) in respect of Customer, Customer's
        liability to pay Pencil any sums due and payable under this Agreement.
      </p>

      <p>
        11.2 Exclusions. Subject only to Section 11.1, Pencil shall not in any
        circumstances be liable to Customer whether in contract, tort (including
        for negligence), breach of statutory duty (howsoever arising),
        misrepresentation (whether innocent or negligent), restitution or
        otherwise, for: (a) any (i) loss, damage or liability (in each case
        whether direct or indirect) of profits, business, business
        opportunities, revenue, turnover, reputation or goodwill, anticipated
        savings or wasted expenditure (including management time); or (ii)
        special, indirect or consequential loss or damage whatsoever, in each
        case (i) and (ii) however arising under or in connection with this
        Agreement and even if Pencil was aware of the possibility that such loss
        or damage might be incurred; or (b) any loss, damage or liability (in
        each case whether direct or indirect) arising as a result of (i)
        Customer's or any third party's use of any Output (save to the limited
        extent, in respect of Customers with a 'Pencil Pro' Subscription only,
        such liability arises under Section 10.1(a)); (ii) any loss or
        corruption of data or information; (iii) any breach, non‑performance or
        any other default by Customer under this Agreement; (iv) any failure by
        Customer to comply with any applicable law, regulation or applicable
        code of practice; and/or (v) Customer's failure to ensure each
        Authorised User maintains the confidentiality of its Access Credentials.
      </p>

      <p>
        11.3 Cap. Subject to Section 11.1 and 11.2, Pencil's total aggregate
        liability to Customer: (a) under the indemnity given by Pencil in
        Section 10.1(a) to Customers with a 'Pencil Pro' Subscription, shall
        under no circumstances exceed one hundred per cent (100%) of all Fees
        paid by Customer under this Agreement in the twelve (12) months
        immediately preceding the first event (or first event in any series of
        connected events) giving rise to an Indemnified Claim for the specific
        Workspace used to generate the Output that is the subject of that
        Indemnified Claim; and (b) in respect of any and all other
        Claims(whether in contract, tort (including negligence or breach of
        statutory duty), misrepresentation (whether innocent or negligent),
        restitution or otherwise) arising in connection with the performance or
        contemplated performance of this Agreement shall under no circumstances
        exceed one hundred per cent (100%) of the Fees paid by Customer under
        this Agreement in the twelve (12) months immediately preceding the first
        event (or first event in any series of connected events) giving rise to
        a Claim against Pencil for the specific Workspace(s) which form the
        subject matter of that Claim – provided that, the existence of more than
        one Indemnified Claim, Claim, or event (or series of connected events)
        from which liability arises shall not enlarge the limits set out in this
        Section 11.3.
      </p>

      <div className="font-bold">12. TERM AND TERMINATION</div>
      <p>
        12.1 Commencement and duration. Unless terminated earlier pursuant to
        the terms of Section 12.2, this Agreement shall: (a) commence on the
        effective date of this Agreement and continue for the Initial Term; and
        (b) automatically renew for further successive Renewal Terms at the end
        of the Initial Term and at the end of each Renewal Term, unless: (y) in
        respect of Customers with a 'Pencil Pro' Subscription, either Party
        gives written notice to the other Party not later than sixty (60) days
        before the end of the Initial Term or then‑current Renewal Term to
        terminate this Agreement upon the expiry of the Initial Term or
        then‑current Renewal Term; or (z) in respect of Customers with a 'Pencil
        Standard' Subscription, either: (i) Customer elects to cancel its
        subscription prior to the commencement of any Renewal Term through its
        configuration of the relevant settings within Customer's account for the
        Platform; or (ii) Pencil givesCustomer notice before the end of the
        Initial Term or then‑current Renewal Term to terminate this Agreement
        upon the expiry of the Initial Term or then‑current Renewal Term.
      </p>

      <p>
        12.2 Termination. Without affecting any other right or remedy available
        to it, either Party may terminate this Agreement with immediate effect
        by giving written notice to the other Party if the otherParty: (a)
        commits a material breach of any term of this Agreement which breach is
        irremediable or (if such breach is remediable) fails to remedy that
        breach within a period of thirty (30) days after being notified to do
        so; or (b) becomes insolvent or unable to pay its debts, proposes a
        voluntary arrangement, has a receiver, administrator or manager
        appointed over the whole or any part of its business or assets, suffers
        the presentation of any petition, the making of any order shall or the
        passing of any resolution for its winding up (except for the purposes of
        a bona fide solvent amalgamation or reconstruction), bankruptcy or
        dissolution, otherwise proposes or enters into any composition or
        arrangement with its creditors or any class of them, ceases to carry on
        business or claims the benefit of any statutory moratorium, or undergoes
        any similar or equivalent process in any jurisdiction. The Parties
        acknowledge and agree that failure byCustomer to pay any Fees when due
        shall constitute a 'material breach' for the purposes of Section
        12.2(a).
      </p>

      <div className="font-bold">13. CONSEQUENCES OF TERMINATION</div>
      <p>
        13.1 Consequences.On termination of this Agreement: (a) notwithstanding
        Section 4,Customer shall promptly (and in any event within ten (10)
        Business Days of the date of termination) pay Pencil any outstanding
        balances owing to Pencil under this Agreement; (b) any and all licences,
        permissions and authorisations granted to Customer and/or its Authorised
        Users by Pencil under this Agreement will terminate automatically; (c)
        Pencil shall delete and destroy any Dedicated Model(s) and shall certify
        in writing (on request from Customer) that it has complied with its
        obligations under this Section 13.1(c); (d) cease to collect Connected
        Data for Customer and disable any connections to Connected Applications
        ; and (e) subject toSection 13.1(c), eachParty will promptly return all
        Confidential Information received from the otherParty, together with all
        copies, or certify (on request from the other Party) in writing that all
        such Confidential Information and copies thereof have been destroyed.
      </p>

      <p>
        13.2 Limitations. Any obligation to return, destroy or permanently erase
        Confidential Information outlined in Section 13.1shall not apply: (a) in
        respect of any Confidential Information, that Pencil may be entitled to
        retain as necessary to comply with any legal, regulatory, judicial,
        audit, or internal compliance requirements; (b) in respect of any
        Confidential Information retained by a Third‑Party Service or Connected
        Application, in accordance with any relevant Third-Party Terms; and (c)
        to any Confidential Information that is retained by Pencil on electronic
        back‑up media made in the ordinary course of business and from which it
        cannot readily be isolated from other information and deleted, provided
        that, in each case, the provisions ofSection 14shall continue to apply
        to Pencil's retention of any such ConfidentialInformation at all
        relevant times.
      </p>

      <p>
        13.3 Survival and accrued rights. Any provision of thisAgreement that
        either expressly or by implication is intended to come into or continue
        in force on or after termination of this Agreement shall remain in full
        force and effect. Termination of this Agreement shall not affect any
        rights, remedies, obligations or liabilities of the Parties that have
        accrued up to the date of termination, including the right to claim
        damages in respect of any breach of this Agreement which existed at or
        before the date of termination.
      </p>

      <div className="font-bold">14. CONFIDENTIAL INFORMATION</div>
      <p>
        In this Agreement, "Confidential Information" mean sany information that
        is clearly labelled or identified as confidential, or reasonably ought
        to be treated as being confidential, and includes the Pencil Technology.
        Confidential Information excludes: (a) any information which: (i) is or
        becomes publicly known other than through a breach of this Agreement;
        (ii) was in the receiving Party's lawful possession before the
        disclosure; (iii) is lawfully disclosed to the receiving Party by a
        third party without restriction on disclosure; (iv) is independently
        developed by the receiving Party and that independent development can be
        shown by written evidence; or (v) is required to be disclosed by law, by
        any court of competent jurisdiction or by any regulatory or
        administrative body; and (b) in the context of Customers with a 'Pencil
        Standard' Subscription only, Outputs to the extent used under the
        licence set out in Section 3.5. Each Party will hold the other's
        Confidential Information in confidence and, except as otherwise provided
        in this Agreement, not make the other'sConfidential Information
        available to any third party unless that third party is subject to an
        equivalent duty of confidentiality; provided that Pencil may make
        available Customer's Confidential Information to the providers of any
        relevant Third‑Party Service and/or Connected Application to be used
        under and in accordance with any relevant Third‑Party Terms. In respect
        of any Third-Party Service, Pencil shall ensure that any Third‑Party
        Terms provide for substantially equivalent duties of confidentiality to
        those set out in this Section 14. Neither Party will use the other's
        Confidential Information for any purpose other than the implementation
        and performance of this Agreement.
      </p>

      <div className="font-bold">15. GENERAL</div>
      <p>
        15.1 Marketing.Customer acknowledges and agrees that Pencil may, and
        Customer hereby grants Pencil a right and licence to, include Customer's
        name, the Authorised Brand and a description of the technology and
        services provided to Customer in respect of the Authorised Brand under
        this Agreement in case study marketing content, lists of or references
        to any of Pencil's clients on its website and/or in proposals, and in
        other marketing materials.
      </p>

      <p>
        15.2 Feedback. If Customer provides feedback, suggestions or
        recommendations to Pencil regarding the Pencil Technology and/or
        Services ("Feedback"), Customer hereby grants Pencil an unrestricted,
        unconditioned, irrevocable and perpetual right and licence to freely use
        and exploit such Feedback without any requirement of compensation.
      </p>

      <p>
        15.3 Force Majeure. No Party will be in breach of this Agreement nor
        liable for any failure to perform its obligations under this Agreement
        if that failure results from circumstances beyond its reasonable control
        (including, in respect of Pencil, any failure resulting from the action
        or omission of a provider of any Third‑Party Service and/or any
        Connected Application, or any unavailability of a Third‑Party Service
        and/or any Connected Application, resulting from circumstances beyond
        Pencil's reasonable control) (a"Force Majeure Event"). If aForce Majeure
        Event continues for three (3) months, the unaffected Party may terminate
        this Agreement by giving thirty (30) days' written notice to the other
        Party.
      </p>

      <p>
        15.4 Subcontracting. Pencil may engage any third party to perform its
        obligations under thisAgreement (including to providers of Third-Party
        Services), provided that Pencil shall remain fully liable to Customer
        for performance of such obligations (subject to the exclusions and
        limitations outlined herein).
      </p>

      <p>
        15.5 Assignment. The Customer will not assign, transfer, charge,
        sub‑contract or deal in any other manner with all or any of its rights
        or obligations under this Agreement, without the prior written consent
        of Pencil; provided that, Customer may, with not less than five(5)
        Business Days' prior notice to Pencil, assign the benefit of this
        Agreement(in whole and not in part) in connection with Customer
        undergoing a change of control (with "control" for these purposes
        meaning the power of a person to secure that the affairs of Customer are
        conducted in accordance with the wishes of that person either: (a) by
        means of the holding of shares, or the possession of voting power, in or
        in relation to Customer or any other entity; or (b) as a result of any
        powers conferred by the articles of association or any other document
        regulating Customer or any other entity). Pencil may at anytime freely
        assign, transfer, charge, sub-contract or deal in any other manner with
        all or any of its rights or obligations under this Agreement.
      </p>

      <p>
        15.6 Variation. In respect of Customers with a 'Pencil Standard'
        Subscription only: (a) in addition to any other variation rights set out
        herein, Pencil may amend this Agreement from time to time by posting a
        new copy of these Terms and Conditions on this webpage or a successor
        page or otherwise providing Customer with notice of such amendment;(b)
        if Customer does not agree to any such amendment, Customer must and
        shall stop using the Platform and the Services; and (c) Customer's
        continued use of the Platform and/or the Services after any amendment
        constitutes Customer's binding acceptance of such amendment, provided
        that Pencil may also require Customer to further evidence such
        acceptance in a specified manner prior to continued use of the Platform
        and/or the Services. In respect of Customers with a 'Pencil
        Pro'Subscription only: unless and only to the extent expressly permitted
        in thisAgreement, no variation of this Agreement shall be effective
        unless it is in writing and signed by the Parties (or their authorised
        representatives).
      </p>

      <p>
        15.7 Waiver. A waiver of any right or remedy under this Agreement or by
        law is only effective if given in writing and shall not be deemed a
        waiver of any subsequent breach or default. A failure or delay by a
        Party to exercise any right or remedy provided under this Agreement or
        by law shall not constitute a waiver of that or any other right or
        remedy, nor shall it prevent or restrict any further exercise of that or
        any other right or remedy. No single or partial exercise of any right or
        remedy provided under this Agreement or by law shall prevent or restrict
        the further exercise of that or any other right or remedy.
      </p>

      <p>
        15.8 Entire Agreement. This Agreement constitutes the entire agreement
        and understanding between the Parties relating to the matters
        contemplated by this Agreement and supersedes all previous agreements
        (if any and whether in writing or not) between the Parties in relation
        to such matters. The Parties acknowledge and agree that, except as
        otherwise expressly provided for in this Agreement, they are not
        entering into this Agreement on the basis of, and are not relying on and
        have not relied on, any statement, representation, warranty or other
        provision (in any case whether oral, written, expressed or implied)
        made, given, or agreed to by any person (whether a Party to this
        Agreement or not) in relation to the subject matter of this Agreement,
        provided that nothing in this Agreement shall exclude any Party from
        liability for fraud or fraudulent misrepresentation.
      </p>

      <p>
        15.9 NoPartnership or Agency.Nothing in this Agreement is intended to,
        or shall be deemed to, establish any partnership or joint venture
        between any of the Parties, constitute any Party the agent of another
        Party, or authorise any Party to make or enter into any commitments for
        or on behalf of any other Party. Each Party confirms it is acting on its
        own behalf and not for the benefit of any other person.
      </p>

      <p>
        15.10 Rights of Third Parties. A person who is not a Party to this
        Agreement shall not be entitled to enforce any of its terms under the
        Contracts (Rights of Third Parties) Act 1999.
      </p>

      <p>
        15.11 Governing law and jurisdiction.The Agreement and all matters
        arising from it (including any disputerelating to the existence,
        validity or termination of this Agreement or any contractual or
        non‑contractual obligation) shall be governed by, and construed in
        accordance with, the laws of England.In relation to any legal action or
        proceedings to enforce this Agreement or arising out of or in connection
        with this Agreement (including any disputerelating to the existence,
        validity or termination of this Agreement or any contractual or
        non‑contractual obligation) (for the purposes of this Section 15.11,
        "Proceedings") each of theParties irrevocably submits to the exclusive
        jurisdiction of the courts of England and waives any objection to
        Proceedings in such courts on the grounds of venue or on the grounds
        that the Proceedings have been brought in an inappropriate forum
        provided that a judgment or order of any court may be enforced in any
        court of competent jurisdiction.
      </p>

      <div className="font-bold">Appendix A – Use‑based Restrictions</div>
      <p>
        Customer shall not use the Platform, Services, any Work Products and/or
        any Outputs:
      </p>
      <ol className="list-decimal pl-7 space-y-4">
        <li>
          in any manner that violates any applicable law, rule or regulation;
        </li>
        <li>
          in any manner that infringes, violates or misappropriates any rights
          of any third party (includingIntellectual Property Rights, privacy
          rights or rights in respect of private information, publicity rights
          or any other rights to a persons' name, image or likeness);
        </li>
        <li>
          for any purpose that is or might reasonably be considered to be
          obscene, lewd, lascivious, offensive, pornographic, indecent, vulgar,
          prurient, excessively violent, hateful or inflammatory, or that
          promotes, encourages, or depicts acts of self-harm, such as suicide,
          cutting, and eating disorders;
        </li>
        <li>
          for the purpose of, or in any manner that is reckless or negligent as
          to the risk of, exploiting, harming or attempting to exploit or harm
          natural persons under the age of eighteen (18)or vulnerable natural
          persons (having regard to their social, economic, physical or mental
          characteristics or circumstances);
        </li>
        <li>
          to generate or disseminate demonstrably false or misleading
          information and/or content for the purpose of, or in any manner that
          is reckless or negligent as to the risk of, exploiting any natural
          persons;
        </li>
        <li>
          to generate Outputs that promote, encourage, or depict acts of
          self-harm, such as suicide, cutting, and eating disorders, and/or that
          are offensive to human dignity;
        </li>
        <li>for any military, para-military, militia or similar uses;</li>
        <li>
          in any manner that is threatening, harmful, abusive or likely to
          incite violence or foment political unrest or insurrection;
        </li>
        <li>
          to make any solely‑automated decisions which have legal or any
          similarly significant effects (including that would or would purport
          to create or modify a binding, enforceable obligation);
        </li>
        <li>
          for the purpose of, or in any manner that is reckless or negligent as
          to the risk of, prejudicially discriminating against or harming
          natural persons or groups based on online or offline social behaviour
          or known or predicted personal or personality characteristics;
        </li>
        <li>
          for any purpose that has, orin any manner that is reckless or
          negligent as to the risk of having, a high risk of economic harm
          (including multi-level marketing, gambling, payday lending, and/or
          automated determinations of eligibility for credit, employment,
          educational institutions, or public assistance services);
        </li>
        <li>
          for or in connection with any fraudulent or deceptive activity
          (including scams, coordinated inauthentic behaviour, astroturfing,
          such as fake grassroots support or fake review generation,
          disinformation, spam and pseudo-pharmaceuticals);
        </li>
        <li>
          for or in connection with any political campaigning or lobbying, which
          involves generating high volumes of campaign materials, generating
          campaign materials personalized to or targeted at specific
          demographics;
        </li>
        <li>
          in direct connection with the administration of justice, law
          enforcement, immigration or asylum processes; and/or
        </li>
        <li>
          for any use intended to, or which has the effect of, discriminating
          against natural persons or groups based on legally protected
          characteristics or categories (including any 'special categories of
          personal data' as defined in the General Data ProtectionRegulation
          2016/679 of the European Union).
        </li>
      </ol>

      <div className="font-bold">
        Appendix B – Third-Party Services and Connected Applications terms
      </div>
      <p>1. Google DV360 and Google Display Network</p>
      <p>
        a. Customer agrees to be bound by the Google Ads policies as outlined at
        https://support.google.com/adspolicy/answer/6008942
      </p>

      <p>2. YouTube</p>
      <p>
        a. Customer agrees to be bound by the YouTube terms of service as
        outlined at https://www.youtube.com/t/terms
      </p>

      <p>3. Getty</p>
      <p>
        a. Customer has the right to use the prompts, or any other inputs that
        are input into the Getty API that are used to generate the Content;
      </p>
      <p>
        b. Customer warrants that the prompts and/or any other inputs used to
        generate such Content do not use any names, likeness of real people,
        trademarks, trade dress, logos, works of art or architecture, or other
        elements protected by third-party intellectual property rights that
        Customer does not have the right to use
      </p>
    </div>
  </Modal>
);

export default BrandtechTermsModal;
